/**
 * Client-side validation for transaction payloads.
 *
 * Each function returns { valid: boolean, errors: string[] }.
 * Catches obvious errors before RPC round-trips.
 *
 * Requirements: 1.1, 3.1, 5.1, 6.2, 6.3, 7.1, 8.1
 */

import { BILLING_STATUS, MAINTENANCE_TYPES, LICENCE_TYPES, USER_ROLES, FUEL_ACCOUNT_TYPES, TIME_FORMATS, MAX_PASSENGERS } from './constants.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Return true when `value` looks like a valid ISO-style date string
 * (YYYY-MM-DD) and parses to a real calendar date.
 */
function isValidDate(value) {
  if (typeof value !== 'string') return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  // Guard against "2024-02-30" which Date silently rolls over
  const [y, m, day] = value.split('-').map(Number);
  return d.getUTCFullYear() === y && d.getUTCMonth() + 1 === m && d.getUTCDate() === day;
}

function isUUID(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}

// ── Flight ──────────────────────────────────────────────────────────────────

/**
 * Validate a flight logging payload.
 * Required: aircraft_id (UUID), pilot_id (UUID), date (valid date),
 *           block_time_mins (> 0), flight_time_mins (> 0).
 */
export function validateFlightPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (!isUUID(payload.aircraft_id)) errors.push('aircraft_id must be a valid UUID');
  if (!isUUID(payload.pilot_id)) errors.push('pilot_id must be a valid UUID');
  if (!isValidDate(payload.date)) errors.push('date must be a valid YYYY-MM-DD date');

  if (typeof payload.block_time_mins !== 'number' || payload.block_time_mins <= 0) {
    errors.push('block_time_mins must be a positive number');
  }
  if (typeof payload.flight_time_mins !== 'number' || payload.flight_time_mins <= 0) {
    errors.push('flight_time_mins must be a positive number');
  }

  return { valid: errors.length === 0, errors };
}

// ── Defect ──────────────────────────────────────────────────────────────────

/**
 * Validate a defect reporting payload.
 * Required: aircraft_id (UUID), description (non-empty string).
 */
export function validateDefectPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (!isUUID(payload.aircraft_id)) errors.push('aircraft_id must be a valid UUID');
  if (typeof payload.description !== 'string' || payload.description.trim() === '') {
    errors.push('description must be a non-empty string');
  }

  return { valid: errors.length === 0, errors };
}

// ── Maintenance ─────────────────────────────────────────────────────────────

const VALID_MAINTENANCE_TYPES = [MAINTENANCE_TYPES.FIFTY_HR, MAINTENANCE_TYPES.ANNUAL, MAINTENANCE_TYPES.OIL_CHANGE, MAINTENANCE_TYPES.OTHER];

/**
 * Validate a maintenance logging payload.
 * Required: aircraft_id (UUID), type ('50HR' | 'ANNUAL'), performed_date (valid date).
 */
export function validateMaintenancePayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (!isUUID(payload.aircraft_id)) errors.push('aircraft_id must be a valid UUID');
  if (!VALID_MAINTENANCE_TYPES.includes(payload.type)) {
    errors.push(`type must be one of: ${VALID_MAINTENANCE_TYPES.join(', ')}`);
  }
  if (!isValidDate(payload.performed_date)) {
    errors.push('performed_date must be a valid YYYY-MM-DD date');
  }

  return { valid: errors.length === 0, errors };
}

// ── Billing period ──────────────────────────────────────────────────────────

/**
 * Validate a billing period (start, end).
 * Both must be valid dates and start must be strictly before end.
 */
export function validateBillingPeriod(start, end) {
  const errors = [];

  const startValid = isValidDate(start);
  const endValid = isValidDate(end);

  if (!startValid) errors.push('start must be a valid YYYY-MM-DD date');
  if (!endValid) errors.push('end must be a valid YYYY-MM-DD date');

  if (startValid && endValid && new Date(start) >= new Date(end)) {
    errors.push('start must be before end');
  }

  return { valid: errors.length === 0, errors };
}

// ── Billing transition ──────────────────────────────────────────────────────

const ALLOWED_TRANSITIONS = {
  [BILLING_STATUS.DRAFT]: BILLING_STATUS.ISSUED,
  [BILLING_STATUS.ISSUED]: BILLING_STATUS.PAID,
};

/**
 * Validate a billing status transition.
 * Only DRAFT→ISSUED and ISSUED→PAID are allowed.
 */
export function validateBillingTransition(currentStatus, newStatus) {
  const errors = [];

  const allStatuses = Object.values(BILLING_STATUS);
  if (!allStatuses.includes(currentStatus)) {
    errors.push(`currentStatus must be one of: ${allStatuses.join(', ')}`);
  }
  if (!allStatuses.includes(newStatus)) {
    errors.push(`newStatus must be one of: ${allStatuses.join(', ')}`);
  }

  if (errors.length === 0 && ALLOWED_TRANSITIONS[currentStatus] !== newStatus) {
    errors.push(`Invalid transition from ${currentStatus} to ${newStatus}`);
  }

  return { valid: errors.length === 0, errors };
}

// ── Import ──────────────────────────────────────────────────────────────────

/**
 * Validate a historical flight import payload.
 * Required: aircraft_reg (non-empty), pilot_name (non-empty),
 *           date (valid date), block_time_mins (> 0).
 */
export function validateImportPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (typeof payload.aircraft_reg !== 'string' || payload.aircraft_reg.trim() === '') {
    errors.push('aircraft_reg must be a non-empty string');
  }
  if (typeof payload.pilot_name !== 'string' || payload.pilot_name.trim() === '') {
    errors.push('pilot_name must be a non-empty string');
  }
  if (!isValidDate(payload.date)) errors.push('date must be a valid YYYY-MM-DD date');
  if (typeof payload.block_time_mins !== 'number' || payload.block_time_mins <= 0) {
    errors.push('block_time_mins must be a positive number');
  }

  return { valid: errors.length === 0, errors };
}

// ── ICAO Code ───────────────────────────────────────────────────────────────

const ICAO_RE = /^[A-Z]{4}$/;

/**
 * Validate an ICAO airport code: exactly 4 uppercase alpha chars (A-Z).
 * @param {string} code
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateICAOCode(code) {
  const errors = [];
  if (typeof code !== 'string' || !ICAO_RE.test(code)) {
    errors.push('ICAO code must be exactly 4 uppercase letters (A-Z)');
  }
  return { valid: errors.length === 0, errors };
}

// ── Booking ─────────────────────────────────────────────────────────────────

/**
 * Validate a booking payload (creates a flight row with SUBMITTED status).
 * Required: aircraft_id (UUID), flight_date (valid date), start_time (HH:MM),
 *           end_time (HH:MM).
 * For single-day bookings: start_time < end_time.
 * For multi-day bookings: end_date must be >= flight_date.
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateBookingPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (!isUUID(payload.aircraft_id)) errors.push('aircraft_id must be a valid UUID');
  // Accept both 'date' (legacy) and 'flight_date'
  const dateField = payload.flight_date || payload.date;
  if (!isValidDate(dateField)) errors.push('flight_date must be a valid YYYY-MM-DD date');

  const timeRe = /^\d{2}:\d{2}$/;
  const validStart = typeof payload.start_time === 'string' && timeRe.test(payload.start_time);
  const validEnd = typeof payload.end_time === 'string' && timeRe.test(payload.end_time);

  if (!validStart) errors.push('start_time must be a valid HH:MM time');
  if (!validEnd) errors.push('end_time must be a valid HH:MM time');

  const isMultiDay = payload.end_date && isValidDate(payload.end_date);

  if (validStart && validEnd && !isMultiDay && payload.start_time >= payload.end_time) {
    errors.push('start_time must be before end_time');
  }

  if (isMultiDay && isValidDate(dateField) && new Date(payload.end_date) < new Date(dateField)) {
    errors.push('end_date must be on or after flight_date');
  }

  return { valid: errors.length === 0, errors };
}

// ── Pilot Licence ───────────────────────────────────────────────────────────

/**
 * Validate a pilot licence payload.
 * Required: licence_type (PPL/CPL/ATPL), rating_category (non-empty string),
 *           expiry_date (valid date).
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validatePilotLicencePayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (!LICENCE_TYPES.includes(payload.licence_type)) {
    errors.push(`licence_type must be one of: ${LICENCE_TYPES.join(', ')}`);
  }

  if (typeof payload.rating_category !== 'string' || payload.rating_category.trim() === '') {
    errors.push('rating_category must be a non-empty string');
  }

  if (!isValidDate(payload.expiry_date)) {
    errors.push('expiry_date must be a valid YYYY-MM-DD date');
  }

  return { valid: errors.length === 0, errors };
}

// ── Pilot-Aircraft Authorisation ────────────────────────────────────────────

/**
 * Check that a pilot has at least one non-expired licence with a rating
 * mapped to the given aircraft type.
 *
 * @param {Array<{ rating_category: string, expiry_date: string }>} licences
 * @param {Array<{ rating_category: string, aircraft_type: string }>} mappings
 * @param {string} aircraftType
 * @returns {{ authorised: boolean, message?: string }}
 */
export function validatePilotAircraftAuthorisation(licences, mappings, aircraftType) {
  if (!Array.isArray(licences) || !Array.isArray(mappings) || typeof aircraftType !== 'string') {
    return { authorised: false, message: 'Invalid arguments' };
  }

  // Find rating categories that map to this aircraft type
  const validRatings = new Set(
    mappings
      .filter((m) => m.aircraft_type === aircraftType)
      .map((m) => m.rating_category),
  );

  if (validRatings.size === 0) {
    return { authorised: false, message: `No rating mapped to aircraft type ${aircraftType}` };
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const hasValid = licences.some((l) => {
    if (!validRatings.has(l.rating_category)) return false;
    const expiry = new Date(l.expiry_date);
    expiry.setHours(0, 0, 0, 0);
    return expiry >= now;
  });

  if (hasValid) {
    return { authorised: true };
  }

  return { authorised: false, message: `You are not rated for aircraft type ${aircraftType}` };
}

// ── Currency Threshold ──────────────────────────────────────────────────────

/**
 * Validate a currency threshold payload.
 * Required: aircraft_type (non-empty string), min_hours_90_days (positive number).
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateCurrencyThresholdPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (typeof payload.aircraft_type !== 'string' || payload.aircraft_type.trim() === '') {
    errors.push('aircraft_type must be a non-empty string');
  }

  if (typeof payload.min_hours_90_days !== 'number' || payload.min_hours_90_days <= 0) {
    errors.push('min_hours_90_days must be a positive number');
  }

  return { valid: errors.length === 0, errors };
}

// ── Email ───────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate an email address format.
 * @param {string} email
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 9.3
 */
export function validateEmail(email) {
  const errors = [];
  if (typeof email !== 'string' || !EMAIL_RE.test(email)) {
    errors.push('email must be a valid email address');
  }
  return { valid: errors.length === 0, errors };
}

// ── Temporary Password ──────────────────────────────────────────────────────

/**
 * Validate a temporary password (minimum 8 characters).
 * @param {string} password
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 9.4
 */
export function validateTemporaryPassword(password) {
  const errors = [];
  if (typeof password !== 'string' || password.length < 8) {
    errors.push('password must be at least 8 characters');
  }
  return { valid: errors.length === 0, errors };
}

// ── Pilot Payload ───────────────────────────────────────────────────────────

const VALID_ROLES = [USER_ROLES.ADMIN, USER_ROLES.OWNER, USER_ROLES.PILOT];

/**
 * Validate a pilot create/edit payload.
 * Required: name (non-empty), email (valid format), role (admin|pilot).
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 9.3, 9.4
 */
export function validatePilotPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (typeof payload.name !== 'string' || payload.name.trim() === '') {
    errors.push('name must be a non-empty string');
  }

  const emailResult = validateEmail(payload.email);
  if (!emailResult.valid) {
    errors.push(...emailResult.errors);
  }

  if (!VALID_ROLES.includes(payload.role)) {
    errors.push(`role must be one of: ${VALID_ROLES.join(', ')}`);
  }

  return { valid: errors.length === 0, errors };
}

// ── Aircraft Payload ────────────────────────────────────────────────────────

/**
 * Validate an aircraft create/edit payload.
 * Required: registration (non-empty), type (non-empty), name (non-empty).
 * Optional hours fields must be numeric >= 0 when present.
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 13.3, 14.5, 22.1
 */
export function validateAircraftPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (typeof payload.registration !== 'string' || payload.registration.trim() === '') {
    errors.push('registration must be a non-empty string');
  }
  if (typeof payload.type !== 'string' || payload.type.trim() === '') {
    errors.push('type must be a non-empty string');
  }
  if (typeof payload.name !== 'string' || payload.name.trim() === '') {
    errors.push('name must be a non-empty string');
  }

  const hourFields = ['airframe_hours', 'engine_hours', 'propeller_hours', 'hours_since_last_50hr'];
  for (const field of hourFields) {
    if (payload[field] !== undefined && payload[field] !== null) {
      const result = validateComponentHours(payload[field]);
      if (!result.valid) {
        errors.push(`${field}: ${result.errors[0]}`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// ── Airport Payload ─────────────────────────────────────────────────────────

/**
 * Validate an airport/airstrip payload.
 * Code is optional (name-only airports allowed); when provided must be 1–10 chars.
 * Required: name (non-empty).
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 18.5, 12.3
 */
export function validateAirportPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  // Code is optional — allow null, undefined, or empty string for name-only airports
  if (payload.code != null && payload.code !== '') {
    if (typeof payload.code !== 'string' || payload.code.trim().length < 1 || payload.code.trim().length > 10) {
      errors.push('code must be between 1 and 10 characters');
    }
  }

  if (typeof payload.name !== 'string' || payload.name.trim() === '') {
    errors.push('name must be a non-empty string');
  }

  return { valid: errors.length === 0, errors };
}

// ── Merge Payload ───────────────────────────────────────────────────────────

/**
 * Validate a user merge payload.
 * Required: parentId (UUID), aliasIds (non-empty array of UUIDs).
 * parentId must not appear in aliasIds.
 * @param {string} parentId
 * @param {string[]} aliasIds
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 8.7
 */
export function validateMergePayload(parentId, aliasIds) {
  const errors = [];

  if (!isUUID(parentId)) {
    errors.push('parentId must be a valid UUID');
  }

  if (!Array.isArray(aliasIds) || aliasIds.length === 0) {
    errors.push('aliasIds must be a non-empty array');
  } else {
    for (let i = 0; i < aliasIds.length; i++) {
      if (!isUUID(aliasIds[i])) {
        errors.push(`aliasIds[${i}] must be a valid UUID`);
      }
    }

    if (isUUID(parentId) && aliasIds.includes(parentId)) {
      errors.push('parentId must not appear in aliasIds');
    }
  }

  return { valid: errors.length === 0, errors };
}

// ── Backup Structure ────────────────────────────────────────────────────────

const BACKUP_TABLES = [
  'aircraft',
  'pilots',
  'flights',
  'flight_fuel_oil',
  'defects',
  'maintenance_events',
  'billing_rates',
  'invoices',
  'airports',
  'aircraft_airports',
  'pilot_licences',
  'licence_aircraft_mappings',
  'currency_thresholds',
];

/**
 * Validate a backup JSON structure.
 * Must have metadata (with export_date, app_version, record_counts) and
 * data section with all 13 table keys.
 * @param {Object} json
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 17.3
 */
export function validateBackupStructure(json) {
  const errors = [];
  if (!json || typeof json !== 'object') {
    return { valid: false, errors: ['Backup must be a non-null object'] };
  }

  // Validate metadata
  if (!json.metadata || typeof json.metadata !== 'object') {
    errors.push('metadata section is required');
  } else {
    if (!json.metadata.export_date) errors.push('metadata.export_date is required');
    if (!json.metadata.app_version) errors.push('metadata.app_version is required');
    if (!json.metadata.record_counts || typeof json.metadata.record_counts !== 'object') {
      errors.push('metadata.record_counts is required');
    }
  }

  // Validate data section
  if (!json.data || typeof json.data !== 'object') {
    errors.push('data section is required');
  } else {
    for (const table of BACKUP_TABLES) {
      if (!Array.isArray(json.data[table])) {
        errors.push(`data.${table} must be an array`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

// ── Component Hours ─────────────────────────────────────────────────────────

/**
 * Validate a component hours value (numeric, >= 0).
 * @param {*} hours
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 22.1
 */
export function validateComponentHours(hours) {
  const errors = [];
  if (typeof hours !== 'number' || Number.isNaN(hours) || hours < 0) {
    errors.push('hours must be a number >= 0');
  }
  return { valid: errors.length === 0, errors };
}

// ── Fuel Account Payload ────────────────────────────────────────────────────

/**
 * Validate a fuel account payload.
 * Type-dependent required fields:
 *   ACCOUNT:          litres (positive), cost in GBP (positive)
 *   OVERSEAS_ACCOUNT: litres (positive), cost in EUR (positive)
 *   NON_ACCOUNT:      litres (positive), total cost in GBP (positive)
 *
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 11.1, 11.2, 11.3, 11.4
 */
export function validateFuelAccountPayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (!FUEL_ACCOUNT_TYPES.includes(payload.type)) {
    errors.push(`type must be one of: ${FUEL_ACCOUNT_TYPES.join(', ')}`);
    return { valid: false, errors };
  }

  if (typeof payload.litres !== 'number' || payload.litres <= 0) {
    errors.push('litres must be a positive number');
  }

  if (payload.type === 'ACCOUNT') {
    if (typeof payload.cost !== 'number' || payload.cost <= 0) {
      errors.push('cost must be a positive number');
    }
    if (payload.currency !== undefined && payload.currency !== 'GBP') {
      errors.push('currency must be GBP for Account type');
    }
  } else if (payload.type === 'OVERSEAS_ACCOUNT') {
    if (typeof payload.cost !== 'number' || payload.cost <= 0) {
      errors.push('cost must be a positive number');
    }
    if (payload.currency !== undefined && payload.currency !== 'EUR') {
      errors.push('currency must be EUR for Overseas Account type');
    }
  } else if (payload.type === 'NON_ACCOUNT') {
    if (typeof payload.totalCost !== 'number' || payload.totalCost <= 0) {
      errors.push('totalCost must be a positive number');
    }
    if (payload.currency !== undefined && payload.currency !== 'GBP') {
      errors.push('currency must be GBP for Non-Account type');
    }
  }

  return { valid: errors.length === 0, errors };
}

// ── Expense Payload ─────────────────────────────────────────────────────────

/**
 * Validate a reimbursable expense payload.
 * Required: description (non-empty string), amount (positive number).
 *
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 11.5
 */
export function validateExpensePayload(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (typeof payload.description !== 'string' || payload.description.trim() === '') {
    errors.push('description must be a non-empty string');
  }

  if (typeof payload.amount !== 'number' || Number.isNaN(payload.amount) || payload.amount <= 0) {
    errors.push('amount must be a positive number');
  }

  return { valid: errors.length === 0, errors };
}

// ── Passenger List ──────────────────────────────────────────────────────────

/**
 * Validate a passenger list.
 * Max MAX_PASSENGERS entries, each must be a non-empty string.
 *
 * @param {Array} passengers
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 10.4, 10.5
 */
export function validatePassengerList(passengers) {
  const errors = [];

  if (!Array.isArray(passengers)) {
    return { valid: false, errors: ['passengers must be an array'] };
  }

  if (passengers.length > MAX_PASSENGERS) {
    errors.push(`passengers list must not exceed ${MAX_PASSENGERS} entries`);
  }

  for (let i = 0; i < passengers.length; i++) {
    if (typeof passengers[i] !== 'string' || passengers[i].trim() === '') {
      errors.push(`passenger at index ${i} must be a non-empty string`);
    }
  }

  return { valid: errors.length === 0, errors };
}

// ── Profile Update ──────────────────────────────────────────────────────────

const VALID_TIME_FORMATS = Object.values(TIME_FORMATS);

/**
 * Validate a pilot profile update payload.
 * Required: name (non-empty), email (valid format), time_format (HH:MM:SS or DECIMAL).
 *
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 16.4, 16.7
 */
export function validateProfileUpdate(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (typeof payload.name !== 'string' || payload.name.trim() === '') {
    errors.push('name must be a non-empty string');
  }

  const emailResult = validateEmail(payload.email);
  if (!emailResult.valid) {
    errors.push(...emailResult.errors);
  }

  if (payload.time_format !== undefined && !VALID_TIME_FORMATS.includes(payload.time_format)) {
    errors.push(`time_format must be one of: ${VALID_TIME_FORMATS.join(', ')}`);
  }

  return { valid: errors.length === 0, errors };
}

// ── Password Change ─────────────────────────────────────────────────────────

/**
 * Validate a password change payload.
 * Required: currentPassword (non-empty), newPassword (min 8 chars).
 *
 * @param {Object} payload
 * @returns {{ valid: boolean, errors: string[] }}
 * Requirements: 16.5, 16.6
 */
export function validatePasswordChange(payload) {
  const errors = [];
  if (!payload || typeof payload !== 'object') {
    return { valid: false, errors: ['Payload must be a non-null object'] };
  }

  if (typeof payload.currentPassword !== 'string' || payload.currentPassword.trim() === '') {
    errors.push('currentPassword is required');
  }

  if (typeof payload.newPassword !== 'string' || payload.newPassword.length < 8) {
    errors.push('newPassword must be at least 8 characters');
  }

  return { valid: errors.length === 0, errors };
}
